import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { EXPLORE_CATEGORIES, GENRES } from "@/lib/constants";
import LocationCombobox from "@/components/shared/LocationCombobox";
import IndianNumberInput from "@/components/shared/IndianNumberInput";
import ProfileCard from "@/components/shared/ProfileCard";
import GigCard from "@/components/shared/GigCard";
import EmptyState from "@/components/shared/EmptyState";
import { Search } from "lucide-react";

export default function Explore() {
  const [sp, setSp] = useSearchParams();
  const [category, setCategory] = useState(sp.get("category") || "all");
  const [location, setLocation] = useState(null);
  const [genre, setGenre] = useState(sp.get("genre") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [minBudget, setMinBudget] = useState(null);
  const [maxBudget, setMaxBudget] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (location?.city) params.city = location.city;
    if (genre) params.genre = genre;
    const nextSp = { category };
    try {
      if (category === "gigs") {
        if (minBudget) params.min_budget = Number(minBudget);
        if (maxBudget) params.max_budget = Number(maxBudget);
        const { data } = await api.get("/opportunities", { params });
        setGigs(data); setProfiles([]);
      } else {
        if (category !== "all") params.profile_type = category;
        const { data } = await api.get("/talent", { params });
        setProfiles(data); setGigs([]);
      }
      setSp(nextSp);
    } finally { setLoading(false); }
  }, [category, location, genre, q, minBudget, maxBudget, setSp]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const results = category === "gigs" ? gigs : profiles;

  return (
    <AppLayout>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl font-bold tracking-tight">Explore</h1>
        <div className="text-sm text-slate-500">{results.length} results</div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200 pb-4">
        {EXPLORE_CATEGORIES.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cat-${c.key}`}
            className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
              category === c.key ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
            }`}>{c.label}</button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <aside className="border border-slate-200 rounded-xl p-5 bg-white h-fit space-y-4">
          <div>
            <Label className="text-sm font-semibold">Search</Label>
            <Input data-testid="filter-q" value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" placeholder="Name or keyword" />
          </div>
          <div>
            <Label className="text-sm font-semibold">Location</Label>
            <div className="mt-1"><LocationCombobox value={location} onChange={setLocation} testId="filter-location" /></div>
          </div>
          <div>
            <Label className="text-sm font-semibold">Genre</Label>
            <select data-testid="filter-genre" value={genre} onChange={(e) => setGenre(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">All genres</option>
              {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          {category === "gigs" && (
            <div>
              <Label className="text-sm font-semibold">Budget (₹)</Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <IndianNumberInput value={minBudget} onChange={setMinBudget} testId="filter-min" placeholder="Min" />
                <IndianNumberInput value={maxBudget} onChange={setMaxBudget} testId="filter-max" placeholder="Max" />
              </div>
            </div>
          )}
          <Button onClick={load} data-testid="filter-apply" className="w-full bg-[#0F172A] hover:bg-[#1E293B] text-white">
            <Search className="w-4 h-4 mr-1" /> Apply filters
          </Button>
        </aside>

        <div>
          {loading && <div className="text-slate-500 py-8 text-center">Loading…</div>}
          {!loading && results.length === 0 && (
            <EmptyState title="No results" description="Try different filters or clear them to see everything." />
          )}
          {category === "gigs" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {gigs.map((op) => <GigCard key={op.opportunity_id} opportunity={op} />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {profiles.map((p) => <ProfileCard key={p.profile_id} profile={p} />)}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
